import * as angular from 'angular';
import { combineLatest, from, Observable, of } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

import { MixableBaseController } from '~/common/component';
import { formatBytes } from '~/common/format.bytes.helper';
import { AccountResource } from '@mode/legacy/data-access-model-js';
import {
  queryResourcesByKeySelector,
  reportQueryRunSelector,
  routerSelector,
  selectedReportDisplayNameSelector,
  selectedReportIndexViewLinkSelector,
  selectedReportLinks,
  selectedReportModelsSelector,
  selectedReportReportRunStateSelector,
} from '~/reducers/selectors';
import { QueryState } from '@mode/legacy/types-app-state';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import { SelectedReportSelectors } from '~/selectors/selected-report.selectors';
import { ToastService } from '@mode/shared/util-toast';
import { AppStore } from '~/types/main.types';

import { ReportViewDataService } from '../../services';
import { FlamingoResponse } from '@mode/shared/contract-common';
import { shareReplayDefault } from '@mode/shared/util-js';

enum tabStates {
  'query-tab' = 'queries.details',
  'dataset-tab' = 'queries.data',
}

enum tabOrder {
  query,
  dataset,
}

class ReportQueryDatasetController extends MixableBaseController implements angular.IOnInit {
  static $inject = ['ReportViewDataService', 'RxStoreService', 'ToastService', '$stateParams'];

  queryTitle$: Observable<string>;
  hasSubTitle$: Observable<boolean>;
  linkToReportIndex$: Observable<string>;
  reportDisplayName$: Observable<string>;
  reportCreator$: Observable<string>;
  queryExecutedBy$: Observable<string>;
  datasetWebLink$: Observable<string>;
  link$: Observable<object>;
  selectedQueryDetails$: Observable<QueryState>;
  reportToken: String;
  rowCount$: Observable<number>;
  queryExecutionTime$: Observable<string>;
  queryRunSize$: Observable<number>;
  reportRunState$: Observable<{
    isVisible: boolean;
    dataset: any;
    executing;
    queryRun: any;
    datasetSizeBytes?: number;
    rowsCountMessage: string;
  }>;
  planId$: Observable<string>;
  flamingoError$: Observable<FlamingoResponse.FlamingoErrorResponse>;
  queryToken$: Observable<string>;

  isClipboardEnabled = true;
  activeTab = 1;
  showParameterCode = false;

  constructor(
    private reportViewDataService: ReportViewDataService,
    private store: AppStore,
    private alertService: ToastService,
    private $stateParams
  ) {
    super();
  }

  $onInit(): void {
    this.store
      .select(routerSelector)
      .pipe(
        filter(({ name }) => name && name.includes('queries')),
        map(({ name }) => (name.endsWith(tabStates['query-tab']) ? tabOrder.query : tabOrder.dataset)),
        takeUntil(this.destroyed$)
      )
      .subscribe((activeTab) => {
        this.activeTab = activeTab;
      });

    this.reportToken = this.$stateParams['reportId'];
    this.queryToken$ = this.store
      .select(routerSelector)
      .pipe(map((routerState) => routerState && routerState.params && routerState.params['queryId']));

    const queriesResources$ = this.store.select(queryResourcesByKeySelector);

    this.selectedQueryDetails$ = combineLatest([queriesResources$, this.queryToken$]).pipe(
      map(([queriesResource, queryToken]) => queriesResource.get(queryToken)),
      filter((state) => state !== undefined),
      first(),
      takeUntil(this.destroyed$)
    );

    this.queryTitle$ = this.selectedQueryDetails$.pipe(map((queryState) => queryState.name));

    this.reportCreator$ = this.selectedQueryDetails$.pipe(
      switchMap((queryState) => from(queryState.resource.links('creator').$follow())),
      map((userAccount: AccountResource) => userAccount.name)
    );

    this.reportDisplayName$ = this.store.select(selectedReportDisplayNameSelector);

    this.linkToReportIndex$ = this.store.select(selectedReportIndexViewLinkSelector);

    this.hasSubTitle$ = combineLatest([this.queryTitle$, this.reportCreator$]).pipe(
      map(([hasTitle, hasCreator]) => !!(hasTitle && hasCreator))
    );

    this.link$ = this.store.select(selectedReportLinks);

    this.datasetWebLink$ = combineLatest([
      this.queryToken$,
      this.store.select(SelectedReportSelectors.getQueryRuns).pipe(map((queryRuns) => queryRuns.byKey)),
    ]).pipe(
      filter(([currentQueryToken, queryRuns]) => queryRuns.has(currentQueryToken)),
      map(([currentQueryToken, queryRuns]) => queryRuns.get(currentQueryToken).reportDataUrl)
    );

    const queryRunDetails$ = combineLatest([
      this.queryToken$,
      this.store.select(SelectedReportSelectors.getQueryRuns),
      this.store.select(selectedReportModelsSelector),
    ]).pipe(
      map(([currentQueryToken, queryRuns, modelStates]) => ({
        modelState: modelStates.hasRecord(currentQueryToken) ? modelStates.getRecord(currentQueryToken) : {},
        queryRun: queryRuns.getRecord(currentQueryToken),
      })),
      filter(({ queryRun }) => queryRun !== undefined && queryRun.state === 'succeeded')
    );

    this.queryExecutedBy$ = queryRunDetails$.pipe(
      switchMap(({ queryRun }) =>
        // GF-incompatible
        queryRun.resource.hasLink('executed_by') ? queryRun.resource.links('executed_by').$follow() : of(null)
      ),
      filter((userAccount: AccountResource | null) => userAccount !== null),
      map((userAccount: AccountResource) => userAccount.name)
    );

    this.rowCount$ = queryRunDetails$.pipe(
      filter(({ modelState }) => modelState && modelState['count'] >= 0),
      map(({ modelState }) => modelState['count'])
    );

    this.queryExecutionTime$ = queryRunDetails$.pipe(map(({ queryRun }) => queryRun.timeCompleted));

    this.queryRunSize$ = queryRunDetails$.pipe(map(({ queryRun }) => queryRun.bytes));

    this.planId$ = this.queryToken$.pipe(
      switchMap((currentQueryToken) => this.store.select(fromFlamingoSelectors.createPlanIdSelector(currentQueryToken)))
    );

    this.flamingoError$ = this.planId$.pipe(
      filter((planId) => planId != null),
      switchMap((id) => this.store.select(fromFlamingoSelectors.createErrorsSelector(id))),
      takeUntil(this.destroyed$)
    );

    const reportStateProps$ = combineLatest([
      this.queryToken$,
      this.store.select(reportQueryRunSelector),
      this.store.select(selectedReportReportRunStateSelector),
      this.store.select(selectedReportModelsSelector),
    ]).pipe(
      filter(([currentQueryToken, queryRuns, reportRun, modelState]) => queryRuns.hasRecord(currentQueryToken)),
      filter(([currentQueryToken, queryRuns, reportRun, modelState]) => modelState.hasRecord(currentQueryToken)),
      map(([currentQueryToken, queryRuns, reportRun, modelState]) => ({
        queryToken: currentQueryToken,
        queryRun: queryRuns.getRecord(currentQueryToken),
        executing: reportRun.running,
        dataset: modelState.getRecord(currentQueryToken),
      }))
    );

    this.reportRunState$ = reportStateProps$.pipe(
      // modified from Workspace Component
      map((details) => {
        const { queryToken, executing, queryRun, dataset } = details;

        const isVisible =
          queryRun &&
          !queryRun.running &&
          queryRun.state === 'succeeded' &&
          dataset &&
          dataset.content &&
          dataset.count > 0;

        const rowsCountVisible = !!(
          queryRun &&
          !queryRun.running &&
          queryRun.state === 'succeeded' &&
          dataset &&
          dataset.count
        );

        const getStatsForRowMessage = () => ({
          rowsCountVisible,
          executionTime: queryRun.timeCompleted,
          rowsCount: dataset.count,
        });

        const datasetSizeBytes = queryRun && queryRun.bytes;

        return {
          queryToken,
          isVisible,
          dataset,
          executing,
          queryRun,
          datasetSizeBytes,
          ...(rowsCountVisible ? getStatsForRowMessage() : {}),
          rowsCountMessage: rowsCountVisible ? `${dataset.count} rows returned` : ``,
        };
      }),
      shareReplayDefault()
    );
  }

  copySuccess(): void {
    this.alertService.success('Link to Dataset copied successfully');
  }

  onTabClick(routerState: 'queries.details' | 'queries.data'): void {
    const partialState = this.reportViewDataService.getRunStateIfRunTokenExists();
    this.reportViewDataService.navigate({ ...partialState, name: `${partialState.name}.${routerState}` });
  }

  getFormattedBytes(bytes: number): string {
    if (bytes != null && !isNaN(bytes)) {
      return formatBytes(bytes);
    }
  }
}
export const ID = 'reportQueryDatasetContainer';

export const reportQueryDatasetContainer: angular.IComponentOptions = {
  template: `
    <div class="report-query-view">
      <div class="code-container">
        <uib-tabset type="pills" class="tab-container" active="ctrl.activeTab">
          <uib-tab heading="Query" index="0" ng-click="ctrl.onTabClick('${tabStates['query-tab']}')">
            <div class="report-query-header">
              <h5>
                <span class="metadata">{{ ctrl.rowCount$ | async:this | number }} rows</span> | <span class="metadata">{{ ctrl.getFormattedBytes(ctrl.queryRunSize$ | async:this) }}</span> returned by {{ctrl.queryExecutedBy$ | async:this}}
              </h5>
              <div class="show-parameter-checkbox">
                <span class="checkbox-icon">
                  <input type="checkbox" ng-model="ctrl.showParameterCode" name="show-parameter-code" id="showParameterCode">
                  <label class="inline-icon icon-editor-checkbox-light-unchecked" for="showParameterCode">Show parameter code</label>
                </span>
              </div>
            </div>
            <div ui-view class="tab-ui-view"></div>
          </uib-tab>
          <uib-tab heading="Dataset" index="1" ng-click="ctrl.onTabClick('${tabStates['dataset-tab']}')">
            <div class="report-query-header">
              <h5>
                <span class="metadata">{{ ctrl.rowCount$ | async:this | number }} rows</span> | <span class="metadata">{{ ctrl.getFormattedBytes(ctrl.queryRunSize$ | async:this) }}</span> returned by {{ctrl.queryExecutedBy$ | async:this}}
              </h5>
              <div class="dataset-links">
                <a
                download="download"
                ng-href="{{ (ctrl.reportRunState$ | async:this).dataset.csv }}"
                class="btn-icon-only btn btn-default"
                ng-click="ctrl.download($event);$event.stopPropagation()"
                target="_self"
                aria-label="Export results as .CSV"
                uib-tooltip="Export results as .CSV"
                tooltip-append-to-body="true"
                tooltip-placement="top"
                tooltip-popup-delay="750"
                >
                  <capra-icon icon="icon-export"></capra-icon>
                </a>
                <copy-dataset-button
                  query-token="(ctrl.reportRunState$ | async:this).queryToken"
                  rows-count="(ctrl.reportRunState$ | async:this).rowsCount"
                  dataset-size-bytes="(ctrl.reportRunState$ | async:this).datasetSizeBytes"
                  execution-time="(ctrl.reportRunState$ | async:this).executionTime"
                  csv-to-download="(ctrl.reportRunState$ | async:this).dataset.csv"
                  json-to-copy="(ctrl.reportRunState$ | async:this).dataset.json"
                  columns="(ctrl.reportRunState$ | async:this).dataset.columns"
                  oversized="(ctrl.reportRunState$ | async:this).dataset.oversized"
                  error="ctrl.flamingoError$ | async:this"
                ></copy-dataset-button>
              </div>
            </div>
            <div ui-view class="tab-ui-view"></div>
          </uib-tab>
        </uib-tabset>
      </div>
    </div>
  `,
  controller: ReportQueryDatasetController,
  controllerAs: 'ctrl',
};
